import {
  MRT_Row as MRTRow,
  MRT_TableInstance as MRTTableInstance,
} from "material-react-table";
import classNames from "classnames";

export type CORETableRowStyleVariant = "default" | "section";

export type CORETableRowStyleResolverResult = {
  variant?: CORETableRowStyleVariant;
  className?: string;
  sx?: Record<string, unknown>;
};

export type CORETableRowStripingContext<
  RecordType extends Record<string, unknown>
> = {
  row: MRTRow<RecordType>;
  rowIndex: number;
  staticRowIndex: number;
  table: MRTTableInstance<RecordType>;
};

export type CORETableRowStripingStrategy<
  RecordType extends Record<string, unknown>
> =
  | {
      mode?: "global";
    }
  | {
      mode: "resetByPredicate";
      resetPredicate: (
        params: CORETableRowStripingContext<RecordType>
      ) => boolean;
      stripePredicate?: (
        params: CORETableRowStripingContext<RecordType>
      ) => boolean;
    };

export const mergeTableRowProps = (
  baseProps: Record<string, unknown>,
  extraProps: Record<string, unknown>
) => {
  return {
    ...baseProps,
    ...extraProps,
    className: classNames(
      baseProps.className as string | undefined,
      extraProps.className as string | undefined
    ),
    sx: {
      ...(baseProps.sx as Record<string, unknown> | undefined),
      ...(extraProps.sx as Record<string, unknown> | undefined),
    },
  };
};

export const buildCustomStripedRowsById = <
  RecordType extends Record<string, unknown>
>(
  table: MRTTableInstance<RecordType>,
  rowStripingStrategy: Extract<
    CORETableRowStripingStrategy<RecordType>,
    { mode: "resetByPredicate" }
  >
) => {
  const stripedRowsById = new Map<string, boolean>();
  const visibleRows = table.getRowModel().rows;
  let stripeIndex = 0;

  visibleRows.forEach((visibleRow, visibleStaticRowIndex) => {
    const context: CORETableRowStripingContext<RecordType> = {
      row: visibleRow,
      rowIndex: visibleRow.index,
      staticRowIndex: visibleStaticRowIndex,
      table,
    };

    if (rowStripingStrategy.resetPredicate(context)) {
      stripeIndex = 0;
      stripedRowsById.set(visibleRow.id, false);
      return;
    }

    const shouldParticipateInStripe = rowStripingStrategy.stripePredicate
      ? rowStripingStrategy.stripePredicate(context)
      : true;

    if (!shouldParticipateInStripe) {
      stripedRowsById.set(visibleRow.id, false);
      return;
    }

    stripedRowsById.set(visibleRow.id, stripeIndex % 2 === 1);
    stripeIndex += 1;
  });

  return stripedRowsById;
};

export const getIsStripedRow = <RecordType extends Record<string, unknown>>(
  row: MRTRow<RecordType>,
  isTableBandedRows: boolean,
  rowStripingStrategy?: CORETableRowStripingStrategy<RecordType>,
  customStripedRowsById?: Map<string, boolean>
) => {
  if (!isTableBandedRows) {
    return false;
  }

  if (rowStripingStrategy?.mode !== "resetByPredicate") {
    return row.index % 2 === 1;
  }

  return customStripedRowsById?.get(row.id) ?? false;
};
